import React, { useState, useEffect, useContext } from "react";
import { getApiUrl } from "../utils/api";
import Modal from "../components/Modal";
import { UserContext } from "../context/UserContext";
import {
  FaFlag,
  FaCheck,
  FaTimes,
  FaTrash,
  FaEye,
  FaUsers,
  FaChartBar,
} from "react-icons/fa";
import { convertFromRaw } from "draft-js";
import { stateToHTML } from "draft-js-export-html";
import { contentToHtml } from "../lib/contentToHtml";
import "./ModeratorDashboard.css";

function ModeratorDashboard() {
  const { user } = useContext(UserContext);
  const [activeTab, setActiveTab] = useState("reports");
  const [reportedContent, setReportedContent] = useState([]);
  const [reportsPagination, setReportsPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    hasNextPage: false,
    total: 0,
  });
  const [loadingMoreReports, setLoadingMoreReports] = useState(false);
  const [moderationStats, setModerationStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedPoem, setSelectedPoem] = useState(null);
  const [showPoemModal, setShowPoemModal] = useState(false);
  const [modal, setModal] = useState({ open: false, title: "", message: "", type: "info" });
  const [confirmModal, setConfirmModal] = useState({ open: false, title: "", message: "", onConfirm: null });

  // Helper function to render poem content
  const renderPoemContent = (content) => {
    if (!content) return "";

    try {
      // Try to parse as JSON (Draft.js format)
      const parsedContent = JSON.parse(content);
      if (parsedContent.blocks) {
        // It's Draft.js format, convert to HTML
        let html = contentToHtml(parsedContent);

        // Remove any remaining alignment markers that might have slipped through
        html = html.replace(
          /\[\[ALIGN:(left|center|right|justify)\]\]\s*/gi,
          ""
        );

        // Add word-break styling to handle long strings
        html = html.replace(
          /<p/g,
          '<p style="word-break: break-word; overflow-wrap: break-word;"'
        );

        return html;
      }
    } catch (e) {
      // If parsing fails, it's probably plain text
    }

    // Clean up alignment markers and format content
    let cleanContent = content;

    // Remove alignment markers from anywhere in the content
    cleanContent = cleanContent.replace(
      /\[\[ALIGN:(left|center|right|justify)\]\]\s*/gi,
      ""
    );

    // Escape HTML characters first to prevent XSS
    cleanContent = cleanContent
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

    // Handle markdown formatting after escaping
    cleanContent = cleanContent
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>") // Bold
      .replace(/\*(.*?)\*/g, "<em>$1</em>") // Italic
      .replace(/__(.*?)__/g, "<u>$1</u>"); // Underline

    // Convert line breaks to <br> tags
    cleanContent = cleanContent.replace(/\n/g, "<br>");

    return `<p style="word-break: break-word; overflow-wrap: break-word;">${cleanContent}</p>`;
  };

  useEffect(() => {
    if (user && (user.role === "moderator" || user.role === "admin" || user.role === "superadmin")) {
      fetchReportedContent();
      fetchModerationStats();
    }
  }, [user]);

  const fetchReportedContent = async (page = 1, append = false) => {
    try {
      if (append) setLoadingMoreReports(true);
      const response = await fetch(
        getApiUrl(`/api/moderator/reports?page=${page}&limit=10&status=pending`),
        {
          credentials: "include",
        }
      );
      const data = await response.json();
      const list = data.poems || [];
      setReportedContent((prev) => (append ? [...prev, ...list] : list));
      const totalPages = Number(data.totalPages) || data.pagination?.totalPages || 1;
      const currentPage = Number(data.currentPage) || data.pagination?.currentPage || page;
      setReportsPagination({
        currentPage,
        totalPages,
        hasNextPage: currentPage < totalPages,
        total: data.total ?? data.pagination?.totalItems ?? list.length,
      });
    } catch (error) {
      console.error("Error fetching reported content:", error);
    } finally {
      setLoading(false);
      setLoadingMoreReports(false);
    }
  };

  const fetchModerationStats = async () => {
    try {
      const response = await fetch(
        getApiUrl("/api/moderator/stats"),
        {
          credentials: "include",
        }
      );
      const data = await response.json();
      setModerationStats(data);
    } catch (error) {
      console.error("Error fetching moderation stats:", error);
    }
  };

  const handleResolveReport = async (poemId, reportId, action) => {
    try {
      const response = await fetch(
        getApiUrl(`/api/moderator/reports/${poemId}/${reportId}/resolve`),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            action: action,
            reason:
              action === "approve" ? "Content approved" : "Report dismissed",
          }),
        }
      );

      if (response.ok) {
        // Refresh the reported content
        fetchReportedContent();
        fetchModerationStats();
      } else {
        const error = await response.json();
        setModal({ open: true, title: "Error", message: error.error || "Failed to resolve report", type: "error" });
      }
    } catch (error) {
      console.error("Error resolving report:", error);
      setModal({ open: true, title: "Error", message: "Failed to resolve report", type: "error" });
    }
  };

  const handleDeleteContent = async (poemId) => {
    setConfirmModal({
      open: true,
      title: "Delete Content?",
      message: "Are you sure you want to delete this content? This action cannot be undone.",
      onConfirm: async () => {
        try {
          const response = await fetch(
            getApiUrl(`/api/moderator/content/${poemId}`),
            {
              method: "DELETE",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({ reason: "Content deleted by moderator" }),
            }
          );
          if (response.ok) {
            fetchReportedContent();
            fetchModerationStats();
            setShowPoemModal(false);
          } else {
            const error = await response.json();
            setModal({ open: true, title: "Error", message: error.error || "Failed to delete content", type: "error" });
          }
        } catch (error) {
          console.error("Error deleting content:", error);
          setModal({ open: true, title: "Error", message: "Failed to delete content", type: "error" });
        } finally {
          setConfirmModal({ open: false, title: "", message: "", onConfirm: null });
        }
      },
    });
  };

  const viewPoemDetails = async (poemId) => {
    try {
      const response = await fetch(
        getApiUrl(`/api/moderator/reports/poem/${poemId}`),
        {
          credentials: "include",
        }
      );
      const data = await response.json();
      setSelectedPoem(data);
      setShowPoemModal(true);
    } catch (error) {
      console.error("Error fetching poem details:", error);
    }
  };

  if (!user || (user.role !== "moderator" && user.role !== "admin" && user.role !== "superadmin")) {
    return (
      <div className="min-h-screen bg-page flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Access Denied
          </h1>
          <p className="text-gray-600">
            You don't have permission to access this page.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-page flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading moderation dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Moderation Dashboard
          </h1>
          <p className="mt-2 text-gray-600">
            Welcome, {user.username}. Manage reported content and moderate the
            community.
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="peer-home-card p-6">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <FaFlag className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Pending Reports
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {moderationStats.reports?.pending || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="peer-home-card p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <FaCheck className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Resolved</p>
                <p className="text-2xl font-bold text-gray-900">
                  {moderationStats.reports?.resolved || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="peer-home-card p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <FaUsers className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Suspended Users
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {moderationStats.users?.suspended || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="peer-home-card p-6">
            <div className="flex items-center">
              <div className="p-2 bg-[#fff5ef] rounded-lg">
                <FaChartBar className="h-6 w-6 text-[#f26522]" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Total Reports
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {moderationStats.reports?.total || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab("reports")}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "reports"
                    ? "border-[#f26522] text-[#f26522]"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Reported Content
              </button>
              <button
                onClick={() => setActiveTab("guide")}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "guide"
                    ? "border-[#f26522] text-[#f26522]"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Guide
              </button>
            </nav>
          </div>

          <div className="p-6">
            {activeTab === "reports" && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Reported Content ({reportsPagination.total || reportedContent.length})
                </h3>

                {reportedContent.length === 0 ? (
                  <div className="text-center py-8">
                    <FaFlag className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No reported content found.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {reportedContent.map((poem) => (
                      <div
                        key={poem._id}
                        className="border border-gray-200 rounded-lg p-4 max-w-full"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <h4 className="text-lg font-medium text-gray-900">
                              {poem.title}
                            </h4>
                            <p className="text-sm text-gray-600 mt-1">
                              by {poem.author?.username}
                            </p>
                            <div className="mt-2">
                              <div
                                className="text-sm text-gray-700 line-clamp-3 moderator-content"
                                dangerouslySetInnerHTML={{
                                  __html: renderPoemContent(poem.content),
                                }}
                              />
                            </div>
                            <div className="mt-3">
                              <div className="flex flex-wrap gap-2">
                                {poem.reports.map((report, index) => (
                                  <span
                                    key={index}
                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                      report.status === "pending"
                                        ? "bg-red-100 text-red-800"
                                        : report.status === "reviewed"
                                        ? "bg-green-100 text-green-800"
                                        : "bg-gray-100 text-gray-800"
                                    }`}
                                  >
                                    {report.reason} - {report.status}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                          <div className="ml-4 flex space-x-2">
                            <button
                              onClick={() => viewPoemDetails(poem._id)}
                              className="p-2 text-gray-400 hover:text-[#f26522]"
                              title="View Details"
                            >
                              <FaEye className="h-4 w-4" />
                            </button>
                            {poem.reports.some(
                              (r) => r.status === "pending"
                            ) && (
                              <>
                                <button
                                  onClick={() =>
                                    handleResolveReport(
                                      poem._id,
                                      poem.reports.find(
                                        (r) => r.status === "pending"
                                      )._id,
                                      "approve"
                                    )
                                  }
                                  className="p-2 text-gray-400 hover:text-green-600"
                                  title="Approve Content"
                                >
                                  <FaCheck className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() =>
                                    handleResolveReport(
                                      poem._id,
                                      poem.reports.find(
                                        (r) => r.status === "pending"
                                      )._id,
                                      "dismiss"
                                    )
                                  }
                                  className="p-2 text-gray-400 hover:text-yellow-600"
                                  title="Dismiss Report"
                                >
                                  <FaTimes className="h-4 w-4" />
                                </button>
                                {(user.role === "admin" || user.role === "superadmin") && (
                                  <button
                                    onClick={() =>
                                      handleDeleteContent(poem._id)
                                    }
                                    className="p-2 text-gray-400 hover:text-red-600"
                                    title="Delete Content"
                                  >
                                    <FaTrash className="h-4 w-4" />
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {reportsPagination.hasNextPage && (
                  <div className="mt-4 text-center">
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-semibold text-[#f26522] hover:underline disabled:opacity-60"
                      disabled={loadingMoreReports}
                      onClick={() =>
                        fetchReportedContent(
                          (reportsPagination.currentPage || 1) + 1,
                          true
                        )
                      }
                    >
                      {loadingMoreReports ? "Loading..." : "Load more reports"}
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === "guide" && (
              <div className="prose max-w-none">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Moderation Guide</h2>
                
                <h3 className="text-lg font-medium text-gray-800 mt-6">Access and Roles</h3>
                <ul className="list-disc pl-6 text-gray-700">
                  <li>Available to users with role <code>moderator</code> or <code>admin</code>.</li>
                  <li><strong>Moderators:</strong> Can approve/dismiss reports. Cannot delete content or suspend users.</li>
                  <li><strong>Admins:</strong> All moderator permissions plus delete content and suspend/ban users.</li>
                </ul>

                <h3 className="text-lg font-medium text-gray-800 mt-6">Report Lifecycle</h3>
                <ol className="list-decimal pl-6 text-gray-700">
                  <li>User submits a report → status becomes <b>pending</b>.</li>
                  <li>Moderator reviews and either <b>Approves</b> (marks reviewed) or <b>Dismisses</b> (invalid).</li>
                  <li>Admin may <b>Delete</b> the poem for clear policy violations.</li>
                </ol>
                <p className="text-gray-700 mt-2">If the same reporter sends another report while pending, their reason updates. After a report is resolved, a new report is allowed.</p>

                <h3 className="text-lg font-medium text-gray-800 mt-6">Actions</h3>
                <ul className="list-disc pl-6 text-gray-700">
                  <li><b>Approve</b>: Marks selected pending report as reviewed. Content remains published.</li>
                  <li><b>Dismiss</b>: Marks selected pending report as dismissed (invalid report).</li>
                  <li><b>Delete</b> (Admin only): Permanently removes the poem. Use for clear policy violations.</li>
                </ul>

                <h3 className="text-lg font-medium text-gray-800 mt-6">Decision Matrix</h3>
                <div className="overflow-x-auto mt-4">
                  <table className="min-w-full border border-gray-300 text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="border border-gray-300 px-4 py-2 text-left">Violation Type</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Severity</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Moderator Action</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Escalate?</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-700">
                      <tr>
                        <td className="border border-gray-300 px-4 py-2">Spam</td>
                        <td className="border border-gray-300 px-4 py-2">Low</td>
                        <td className="border border-gray-300 px-4 py-2">Dismiss or Approve</td>
                        <td className="border border-gray-300 px-4 py-2">No</td>
                      </tr>
                      <tr className="bg-[#fff5ef]">
                        <td className="border border-gray-300 px-4 py-2">Minor Harassment</td>
                        <td className="border border-gray-300 px-4 py-2">Medium</td>
                        <td className="border border-gray-300 px-4 py-2">Approve (remove)</td>
                        <td className="border border-gray-300 px-4 py-2">If repeated</td>
                      </tr>
                      <tr>
                        <td className="border border-gray-300 px-4 py-2">Hate Speech</td>
                        <td className="border border-gray-300 px-4 py-2">High</td>
                        <td className="border border-gray-300 px-4 py-2">Approve (remove)</td>
                        <td className="border border-gray-300 px-4 py-2">Yes - for ban</td>
                      </tr>
                      <tr className="bg-red-50">
                        <td className="border border-gray-300 px-4 py-2">Threats of Violence</td>
                        <td className="border border-gray-300 px-4 py-2">Critical</td>
                        <td className="border border-gray-300 px-4 py-2">Approve (remove)</td>
                        <td className="border border-gray-300 px-4 py-2">Yes - immediate</td>
                      </tr>
                      <tr className="bg-red-50">
                        <td className="border border-gray-300 px-4 py-2">Illegal Content</td>
                        <td className="border border-gray-300 px-4 py-2">Critical</td>
                        <td className="border border-gray-300 px-4 py-2">Approve (remove)</td>
                        <td className="border border-gray-300 px-4 py-2">Yes - immediate</td>
                      </tr>
                      <tr>
                        <td className="border border-gray-300 px-4 py-2">Copyright Infringement</td>
                        <td className="border border-gray-300 px-4 py-2">Medium</td>
                        <td className="border border-gray-300 px-4 py-2">Approve (remove)</td>
                        <td className="border border-gray-300 px-4 py-2">Yes - for documentation</td>
                      </tr>
                      <tr>
                        <td className="border border-gray-300 px-4 py-2">Impersonation</td>
                        <td className="border border-gray-300 px-4 py-2">High</td>
                        <td className="border border-gray-300 px-4 py-2">Approve (remove)</td>
                        <td className="border border-gray-300 px-4 py-2">Yes - for ban</td>
                      </tr>
                      <tr>
                        <td className="border border-gray-300 px-4 py-2">Privacy Violation</td>
                        <td className="border border-gray-300 px-4 py-2">High</td>
                        <td className="border border-gray-300 px-4 py-2">Approve (remove)</td>
                        <td className="border border-gray-300 px-4 py-2">Yes - for ban</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <h3 className="text-lg font-medium text-gray-800 mt-6">When to Escalate</h3>
                <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-4">
                  <p className="text-sm font-semibold text-gray-800 mb-2">Must Escalate Immediately:</p>
                  <ul className="list-disc pl-6 text-sm text-gray-700">
                    <li>Threats of violence or harm</li>
                    <li>Content involving minors</li>
                    <li>Copyright/DMCA claims</li>
                    <li>Law enforcement requests</li>
                    <li>Security issues or hacking attempts</li>
                  </ul>
                </div>
                <div className="bg-[#fff5ef] border-l-4 border-[#f26522] p-4 mb-4">
                  <p className="text-sm font-semibold text-gray-800 mb-2">Should Escalate:</p>
                  <ul className="list-disc pl-6 text-sm text-gray-700">
                    <li>Borderline or ambiguous content</li>
                    <li>Repeated violations by same user</li>
                    <li>Complex policy questions</li>
                    <li>User appeals or complaints</li>
                    <li>High-profile cases</li>
                  </ul>
                </div>
                <p className="text-gray-700 text-sm italic">When in doubt, escalate to an admin. Do not take action on escalated cases until admin responds.</p>

                <h3 className="text-lg font-medium text-gray-800 mt-6">Common Scenarios</h3>
                <div className="space-y-4 mt-4">
                  <div className="border-l-4 border-gray-300 pl-4">
                    <p className="font-semibold text-gray-800">Borderline Hate Speech</p>
                    <p className="text-sm text-gray-700">If content is ambiguous or context-dependent, escalate (P1). Provide full context. Wait for admin decision before acting.</p>
                  </div>
                  <div className="border-l-4 border-gray-300 pl-4">
                    <p className="font-semibold text-gray-800">Copyright Claim</p>
                    <p className="text-sm text-gray-700">Review both poems. Escalate to admin (P1) with both poems and evidence. Admin decides on deletion and user action.</p>
                  </div>
                  <div className="border-l-4 border-gray-300 pl-4">
                    <p className="font-semibold text-gray-800">Repeated Violations</p>
                    <p className="text-sm text-gray-700">Review user history. Escalate to admin (P2) with violation history. Recommend action (warning, suspension, ban).</p>
                  </div>
                  <div className="border-l-4 border-red-500 pl-4">
                    <p className="font-semibold text-gray-800">Threat of Violence</p>
                    <p className="text-sm text-gray-700">Immediately escalate (P0). Remove content if possible. Document all details. Admin reviews for ban and potential law enforcement notification.</p>
                  </div>
                </div>

                <h3 className="text-lg font-medium text-gray-800 mt-6">Rate Limits</h3>
                <ul className="list-disc pl-6 text-gray-700">
                  <li>Reports: up to 5 per hour per user.</li>
                  <li>Enforced on: <code>POST /api/poems/:id/report</code></li>
                </ul>

                <h3 className="text-lg font-medium text-gray-800 mt-6">Best Practices</h3>
                <ul className="list-disc pl-6 text-gray-700">
                  <li>Approve valid content to record the review decision.</li>
                  <li>Dismiss clearly invalid or abusive reports.</li>
                  <li>Delete only for clear violations (hate, harassment, illegal content, doxxing, spam).</li>
                  <li>Include a short resolution reason when applicable.</li>
                  <li><strong>Escalate uncertain cases to admins - do not guess.</strong></li>
                  <li>Review user history before making decisions.</li>
                  <li>Consider context and intent, not just content.</li>
                </ul>

                <h3 className="text-lg font-medium text-gray-800 mt-6">Troubleshooting</h3>
                <ul className="list-disc pl-6 text-gray-700">
                  <li>"Rate Limit Exceeded": wait up to 1 hour or use a different test account.</li>
                  <li>"Already reported": pending report reasons update; resolved reports allow new submissions.</li>
                  <li>Modal buttons unresponsive: check browser console for errors, ensure no extensions blocking scripts.</li>
                </ul>

                <h3 className="text-lg font-medium text-gray-800 mt-6">Quick Reference</h3>
                <div className="bg-[#fff5ef] p-4 rounded-lg mt-4">
                  <p className="text-sm text-gray-700 mb-2"><strong>Moderator Permissions:</strong></p>
                  <ul className="list-disc pl-6 text-sm text-gray-700">
                    <li>✅ Approve/Dismiss reports</li>
                    <li>✅ View all reported content</li>
                    <li>❌ Cannot delete content</li>
                    <li>❌ Cannot suspend/ban users</li>
                  </ul>
                  <p className="text-sm text-gray-700 mt-3 mb-2"><strong>Admin Permissions:</strong></p>
                  <ul className="list-disc pl-6 text-sm text-gray-700">
                    <li>✅ All moderator permissions</li>
                    <li>✅ Delete content permanently</li>
                    <li>✅ Suspend/ban users (temporary or permanent)</li>
                    <li>✅ Handle escalations and appeals</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Poem Details Modal */}
      {showPoemModal && selectedPoem && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {selectedPoem.title}
                </h3>
                <button
                  onClick={() => setShowPoemModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FaTimes className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  by {selectedPoem.author?.username}
                </p>
                <div className="prose max-w-none">
                  <div
                    className="whitespace-pre-wrap font-sans text-gray-800 moderator-content"
                    dangerouslySetInnerHTML={{
                      __html: renderPoemContent(selectedPoem.content),
                    }}
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium text-gray-900 mb-2">Reports:</h4>
                {selectedPoem.reports.map((report, index) => (
                  <div key={index} className="bg-[#fff5ef] p-3 rounded mb-2">
                    <p className="text-sm text-gray-700">
                      <strong>Reason:</strong> {report.reason}
                    </p>
                    <p className="text-sm text-gray-600">
                      <strong>Reported by:</strong>{" "}
                      {report.reportedBy?.username}
                    </p>
                    <p className="text-sm text-gray-600">
                      <strong>Date:</strong>{" "}
                      {new Date(report.date).toLocaleDateString()}
                    </p>
                    <p className="text-sm text-gray-600">
                      <strong>Status:</strong> {report.status}
                    </p>
                  </div>
                ))}
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowPoemModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                >
                  Close
                </button>
                {selectedPoem.reports.some((r) => r.status === "pending") && (
                  <>
                    <button
                      onClick={() => {
                        handleResolveReport(
                          selectedPoem._id,
                          selectedPoem.reports.find(
                            (r) => r.status === "pending"
                          )._id,
                          "approve"
                        );
                        setShowPoemModal(false);
                      }}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => {
                        handleResolveReport(
                          selectedPoem._id,
                          selectedPoem.reports.find(
                            (r) => r.status === "pending"
                          )._id,
                          "dismiss"
                        );
                        setShowPoemModal(false);
                      }}
                      className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700"
                    >
                      Dismiss
                    </button>
                    {user.role === "admin" && (
                      <button
                        onClick={() => {
                          handleDeleteContent(selectedPoem._id);
                        }}
                        className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        Delete
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {modal.open && (
        <Modal open={modal.open} title={modal.title} message={modal.message} type={modal.type} onClose={() => setModal({ ...modal, open: false })} />
      )}
      {confirmModal.open && (
        <Modal
          open={confirmModal.open}
          title={confirmModal.title || "Confirm Action"}
          message={confirmModal.message || "Are you sure?"}
          type="error"
          onClose={() => setConfirmModal({ open: false, title: "", message: "", onConfirm: null })}
          onConfirm={async () => {
            try {
              if (typeof confirmModal.onConfirm === "function") await confirmModal.onConfirm();
            } finally {
              setConfirmModal({ open: false, title: "", message: "", onConfirm: null });
            }
          }}
          confirmText="Confirm"
        />
      )}
    </div>
  );
}

export default ModeratorDashboard;
